import { useEffect, useMemo, useState } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { useSession } from "@/context/SessionProvider";

const money = (v) =>
    `$${Number(v || 0).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

export default function EmpresaBalanceHeader({ empresa }) {
    const { user, loading: sessionLoading, error: sessionError } = useSession();
    const isAdmin = [1, 5].includes(user?.perfil_id ?? null);

    // 1) si viene por prop (admin puede elegir)
    const propEmpresaId = empresa?.empresa_id ?? empresa?.id ?? null;

    // 2) si no, usa la empresa de sesión
    const empresa_id = isAdmin ? (propEmpresaId ?? null) : (user?.empresa_id ?? null);

    const [loading, setLoading] = useState(true);
    const [raw, setRaw] = useState(null);
    const [error, setError] = useState(null);

    // fetch del resumen (el backend de todas formas ignora empresa_id si no eres admin)
    useEffect(() => {
        if (sessionLoading) return; // espera tener la sesión
        if (!empresa_id) {
            setRaw(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const { data } = await axios.get(API_ROUTES.resumenEmpresa(empresa_id), {
                    withCredentials: true,
                });
                setRaw(data || null);
            } catch (e) {
                console.error("[EmpresaBalanceHeader] error:", e);
                setError(e?.response?.data?.message || e?.message || "Error");
            } finally {
                setLoading(false);
            }
        })();
    }, [empresa_id, sessionLoading]);

    const resumen = useMemo(() => {
        if (!raw) return null;

        const deposito_total = Number(raw.deposito_total ?? raw.debito_total ?? 0);
        const cargos_total = Number(raw.cargos_total ?? 0);
        const credito_base = Number(raw.credito_base ?? 0);
        const credito_usado = Number(raw.credito_usado ?? 0);
        const extra_temporal = Number(raw.extras_activos?.temporal ?? 0);
        const extra_promocional = Number(raw.extras_activos?.promocional ?? 0);

        // Balance de depósito (puede venir del backend o calcularlo)
        const balance_deposito = Number(raw.balance_deposito ?? deposito_total - cargos_total);

        // Determinar si tiene depósito
        const hasDeposit = deposito_total > 0.0001;
        const hasCreditLimit = credito_base > 0 || extra_temporal > 0;

        // Crédito disponible "real" (puede ser negativo si se sobregira)
        const credito_disponible = credito_base - credito_usado + extra_temporal;

        // Para el cálculo de saldo total NO contamos crédito disponible negativo
        const credito_disponible_para_saldo = Math.max(0, credito_disponible);

        // Saldo total = depósito disponible + crédito disponible + promo
        const saldo_total = balance_deposito + credito_disponible_para_saldo + extra_promocional;

        // Flags para determinar el estado
        const usando_credito = credito_usado > 0.01; // Tolerancia para decimales
        const credito_agotado =
            credito_disponible_para_saldo === 0 &&
            (credito_base + extra_temporal) > 0 &&
            credito_usado >= (credito_base + extra_temporal);

        // NUEVO: Determinar si es deudor
        // Es deudor si:
        // 1. NO tiene depósito pero SÍ tiene cargos (deuda pura)
        // 2. O si está usando crédito y el balance de depósito es 0 o negativo
        const es_deudor = (!hasDeposit && cargos_total > 0) ||
            (usando_credito && balance_deposito <= 0);

        return {
            deposito_total,
            cargos_total,
            balance_deposito,
            credito_disponible,
            extra_promocional,
            saldo_total,
            usando_credito,
            credito_agotado,
            es_deudor,
            hasDeposit,
            hasCreditLimit,
        };
    }, [raw]);

    const chip = useMemo(() => {
        if (sessionLoading) return <span className="text-xs text-slate-500 animate-pulse">Cargando sesión…</span>;
        if (sessionError) return <span className="text-xs text-rose-600" title={String(sessionError)}>Sin sesión</span>;
        if (!empresa_id) return <span className="text-xs text-slate-500 italic">Sin empresa</span>;
        if (loading) return <span className="text-xs text-slate-500 animate-pulse">Cargando saldo…</span>;
        if (error) return <span className="text-xs text-rose-600" title={String(error)}>Error al cargar</span>;
        if (!resumen) return <span className="text-xs text-slate-500 italic">Sin datos</span>;

        const { saldo_total, balance_deposito, usando_credito, credito_agotado, es_deudor } = resumen;

        // 🔴 CASO 1: Es deudor (no tiene depósito Y tiene cargos, O usa crédito sin depósito)
        if (es_deudor) {
            const leyenda = credito_agotado ? "Crédito agotado" : "Deudor";
            return (
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-3 py-1 rounded">
                    {leyenda} • {money(saldo_total)}
                </span>
            );
        }

        // 🔴 CASO 2: Saldo negativo (debe dinero)
        if (saldo_total < 0) {
            return (
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-rose-800 bg-rose-100 border border-rose-200 px-3 py-1 rounded">
                    Deuda: {money(saldo_total)}
                </span>
            );
        }

        // ✅ CASO 3: Saldo positivo (tiene saldo a favor)
        return (
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 bg-emerald-100 border border-emerald-200 px-3 py-1 rounded">
                Saldo: {money(saldo_total)}
            </span>
        );
    }, [sessionLoading, sessionError, empresa_id, loading, error, resumen]);

    return <div className="flex items-center">{chip}</div>;
}